// Pure footer-note composition. Mirrors apps/api/src/modules/invoices/footer-note.ts —
// keep both copies in sync (same reason pickName is duplicated in InvoiceBuilder.tsx).

export type IncludedText = { textEn: string | null; textIt: string | null }

/** Choose a text in the requested language, falling back to the other if empty. */
export function pickText(
  en: string | null | undefined,
  it: string | null | undefined,
  lang: 'en' | 'it',
): string {
  const primary = lang === 'it' ? it : en
  const secondary = lang === 'it' ? en : it
  if (primary && primary.trim()) return primary
  return secondary && secondary.trim() ? secondary : ''
}

const MARKER = /\((\d+)\)/
const MARKER_G = /\((\d+)\)/g

/**
 * Splice the selected lines into the static footer note, continuing its numbering.
 * The legal closer is expected on its own line; items are appended to the LAST line
 * that already carries a (N) marker, so the closer stays last.
 */
export function composeFooterNote(
  staticNote: string | null | undefined,
  services: IncludedText[],
  lang: 'en' | 'it',
): string {
  const note = staticNote ?? ''
  const texts = services
    .map(s => pickText(s.textEn, s.textIt, lang))
    .filter(t => t.trim())
  if (!texts.length) return note.trim()

  let maxN = 0
  for (const m of note.matchAll(MARKER_G)) maxN = Math.max(maxN, Number(m[1]))
  const numbered = texts.map((t, i) => `(${maxN + 1 + i}) ${t.trim()}`).join(', ')

  const lines = note.split('\n')
  let idx = -1
  for (let i = lines.length - 1; i >= 0; i--) {
    if (MARKER.test(lines[i])) { idx = i; break }
  }
  if (idx === -1) return [...lines, numbered].join('\n').trim()

  lines[idx] = `${lines[idx].trimEnd().replace(/[,;]$/, '')}, ${numbered}`
  return lines.join('\n').trim()
}
